import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import lpBondsCollection from 'services/lpBondsCollection';
import useWalletContext from 'hooks/useWalletContext';

/**
 * Context to cache bond routes from the backend API.
 * Provides address → route/displayName lookup without needing BOND_ADDRESSES constant.
 *
 * The backend is the single source of truth for bond configuration.
 */
const BondRoutesContext = createContext({
  routes: [],
  routeMap: new Map(),
  loading: true,
  getRouteByAddress: () => null,
  getChainByAddress: () => null,
  getDisplayNameByAddress: () => 'LP Bonds',
});

export const BondRoutesProvider = ({ children }) => {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const { storedChain } = useWalletContext();

  // Fetch bond routes when chain changes
  useEffect(() => {
    const fetchRoutes = async () => {
      setLoading(true);
      try {
        const response = await lpBondsCollection.getBondRoutes();
        if (response?.data) {
          setRoutes(response.data);
        }
      } catch (error) {
        console.error('[BondRoutesContext] Failed to fetch bond routes:', error);
        setRoutes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRoutes();
  }, [storedChain]);

  // Create lookup map: address -> route data
  const routeMap = useMemo(() => {
    const map = new Map();
    routes.forEach((route) => {
      map.set(route.address.toLowerCase(), route);
    });
    return map;
  }, [routes]);

  // Get route URL by address
  const getRouteByAddress = useCallback(
    (address) => {
      if (!address) {
        return null;
      }
      const routeData = routeMap.get(address.toLowerCase());
      return routeData?.route || null;
    },
    [routeMap],
  );

  // Get chain by address (returns chain string directly from backend)
  const getChainByAddress = useCallback(
    (address) => {
      if (!address) {
        return null;
      }
      const routeData = routeMap.get(address.toLowerCase());
      return routeData?.chain || null;
    },
    [routeMap],
  );

  // Get display name by address (extract from route like '/lp-bonds/apechain/APE-GMI-L3' -> 'APE-GMI-L3')
  const getDisplayNameByAddress = useCallback(
    (address) => {
      if (!address) {
        return 'LP Bonds';
      }
      const routeData = routeMap.get(address.toLowerCase());
      if (!routeData?.route) {
        return 'LP Bonds';
      }

      // Extract bond key from route (e.g., '/lp-bonds/apechain/APE-GMI-L3' -> 'APE-GMI-L3')
      const parts = routeData.route.split('/');
      const bondKey = parts[parts.length - 1];

      // Format as display name (e.g., 'APE-GMI-L3' -> 'APE-GMI-BOND-L3')
      // Supports both uppercase and lowercase level suffixes (e.g., -L3 or -l3)
      const levelMatch = bondKey.match(/-[Ll](\d+)$/);
      if (levelMatch) {
        const baseName = bondKey.replace(/-[Ll]\d+$/, '');
        return `${baseName}-BOND-L${levelMatch[1]}`;
      }
      return `${bondKey}-BOND-L1`;
    },
    [routeMap],
  );

  const value = useMemo(
    () => ({
      routes,
      routeMap,
      loading,
      getRouteByAddress,
      getChainByAddress,
      getDisplayNameByAddress,
    }),
    [routes, routeMap, loading, getRouteByAddress, getChainByAddress, getDisplayNameByAddress],
  );

  return <BondRoutesContext.Provider value={value}>{children}</BondRoutesContext.Provider>;
};

export const useBondRoutes = () => {
  const context = useContext(BondRoutesContext);
  if (!context) {
    throw new Error('useBondRoutes must be used within a BondRoutesProvider');
  }
  return context;
};

export default BondRoutesContext;
